import { UserHelper } from '@/helpers/user.helper';
import { AppConfigService } from '@/services/app-config.service';
import { AuthenticationService } from '@/services/authentication.service';
import { MenuToNative, MenuToNativeId } from '@/services/bridge/bridge.service';
import { NavService } from '@/services/nav.service';
import { NotificationCheckService } from '@/services/notification-check.service';
import { ReferralStateService } from '@/services/referral-state.service';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { BonusWallet } from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, filter } from 'rxjs';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-main-menu-list',
  templateUrl: './main-menu-list.component.html',
  styleUrls: ['./main-menu-list.component.scss'],
  imports: [
    MatListModule,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    MatBadgeModule,
    TranslateModule,
    TooltipDirective,
    CurrencyPipe,
  ],
  host: { '[class.theme-row]': "theme === 'row'" },
})
export class MainMenuListComponent implements OnInit, OnDestroy {
  private navService = inject(NavService);
  private authenticationService = inject(AuthenticationService);
  private appConfigService = inject(AppConfigService);
  private ref = inject(ChangeDetectorRef);
  private userHelper = inject(UserHelper);
  private referralState = inject(ReferralStateService);
  private notificationCheckService = inject(NotificationCheckService);

  readonly PROFILE_ID = MenuToNativeId.PROFILE;

  @Input() theme: 'default' | 'row' = 'default';

  sub = new Subscription();
  listSub = new Subscription();
  list: MenuToNative[] = [];
  /** referral bonus wallet of an agent, shown next to "Mon compte" when its balance is > 0 */
  wallet: BonusWallet | null = null;

  themeCss: 'main-menu-list-theme' | 'main-menu-list-theme-row';

  logout() {
    this.authenticationService.logout();
  }

  ngOnInit(): void {
    this.setThemeCss();
    this.listSub = this.navService.menuItems$.subscribe((list: MenuToNative[]) => {
      this.onMenuChange(list);
    });
    this.navService.currentDataPage$.subscribe(() => {
      this.onMenuChange(this.list);
    });
    if (this.userHelper.isRealEstate()) {
      this.sub.add(
        this.referralState.summary$.subscribe((summary) => {
          this.wallet = summary?.wallet ?? null;
          this.ref.detectChanges();
        }),
      );
      // a credit of the wallet comes with a notification: the summary is reloaded when the count changes
      this.sub.add(
        this.notificationCheckService.notificationCount
          .pipe(filter((count) => count !== null))
          .subscribe(() => this.referralState.refresh()),
      );
    }
  }

  get hasWalletBalance(): boolean {
    return this.wallet?.balance > 0;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.listSub.unsubscribe();
  }

  setThemeCss() {
    switch (this.theme) {
      case 'default':
        this.themeCss = 'main-menu-list-theme';
        break;
      case 'row':
        this.themeCss = 'main-menu-list-theme-row';
        break;
    }
  }

  onMenuChange(list: MenuToNative[]) {
    const data = this.navService.currentDataPage$.getValue();
    if (data) {
      // level 1
      list.forEach((l) => {
        l.show = data.level === 1;
        l.showIconCollapse = data.level !== 1;
        this.ref.detectChanges();
        // level 2
        l?.childs?.forEach((c) => {
          c.show = data.level > 1 && c.id !== MenuToNativeId.PROP_MAIN;
          c.showIconCollapse = c?.childs?.length > 0;
          this.ref.detectChanges();
          // level 3
          c?.childs?.forEach((s) => {
            s.showIconCollapse = s?.childs?.length > 0;
            if (data.level === 3 && c.childs.find((t) => t.id === data.page)) {
              c.collapse = false;
            }
          });
        });
      });
    }

    this.list = list;
    this.ref.detectChanges();
  }

  toggleCollapse(e: MouseEvent, item: MenuToNative) {
    e.stopPropagation();
    e.preventDefault();
    item.collapse = !item.collapse;
  }

  onItemClick(item: MenuToNative) {
    if (item.link === 'logout') {
      this.logout();
    }
    if (this.appConfigService.isMobileOrTablet) {
      this.navService.toggleMainMenu();
    }
  }
}
